/**
 * Ligue comptable d'un match — source unique, importée par le client
 * (lib/leagueScope/matchScope.ts#getMatchAccountingLeagueId) et par les
 * fonctions edge (close-accounting-period, category-recap, relevés). Pur :
 * aucun import, compatible Node et Deno.
 *
 *   match régulier                 → ligue de son groupe (league_groups.league_id)
 *   match de compétition externe   → ligue organisatrice (matches.originating_league_id)
 *                                    SEULEMENT si elle est régionale (niveau 4, LRF_*)
 *                                    ou de wilaya (niveau 5, LWF_*) ; sinon (LIRF,
 *                                    LNFA, LFP, fédération…, ou sans organisatrice)
 *                                    null = hors de toute comptabilité.
 *
 * Les matchs externes vivent dans les groupes de la ligue virtuelle
 * EXT_COMPETITIONS : `league_groups.league_id` n'est jamais leur ligue comptable.
 */

/** Niveaux de ligue dont les matchs externes organisés entrent dans la comptabilité. */
export const ACCOUNTING_EXTERNAL_LEAGUE_LEVELS: readonly number[] = Object.freeze([4, 5]);

/** Vrai si une ligue de ce niveau paie les matchs externes qu'elle organise. */
export function leagueTakesExternalMatchesInAccounting(level: number | null | undefined): boolean {
    return typeof level === 'number' && ACCOUNTING_EXTERNAL_LEAGUE_LEVELS.includes(level);
}

export interface AccountingLeagueInput {
    isExternalCompetition?: boolean | null;
    originatingLeagueId?: string | null;
    /** Ligue du groupe du match (league_groups.league_id). */
    groupLeagueId?: string | null;
}

/** Ligue comptable, ou null quand le match n'est dans la comptabilité d'aucune ligue. */
export function resolveAccountingLeagueId(
    match: AccountingLeagueInput,
    leagueLevelOf: (leagueId: string) => number | null | undefined,
): string | null {
    if (!match.isExternalCompetition) return match.groupLeagueId ?? null;
    const origin = match.originatingLeagueId;
    if (!origin) return null;
    return leagueTakesExternalMatchesInAccounting(leagueLevelOf(origin)) ? origin : null;
}

/** Niveau d'une ligue (null si introuvable ou en erreur : la ligue ne prend alors aucun match externe). */
export async function fetchLeagueLevel(
    // deno-lint-ignore no-explicit-any
    supabase: any,
    leagueId: string,
): Promise<number | null> {
    const { data, error } = await supabase.from('leagues').select('level').eq('id', leagueId).maybeSingle();
    if (error) throw error;
    return typeof data?.level === 'number' ? data.level : null;
}
